/**
 * Customer auth context — Saheeh / Saudi.
 *
 * Mirrors the Sushiki AuthContext pattern (phone OTP → 24h session +
 * lazy-loaded customer profile) but runs on Supabase Auth instead of
 * Firebase Auth + custom Twilio Functions. Supabase's phone provider
 * wraps Twilio Verify under the hood, so the user-facing flow is
 * identical: enter phone → receive SMS code → verify → session JWT
 * stored automatically.
 *
 * Profile is persisted to the `customers` table under the
 * authenticated user's tenant. Anonymous users browse + see the
 * menu freely; the auth wall only kicks in at /checkout.
 */

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '../supabase';

export interface CustomerProfile {
  name: string;
  phone: string;
  email: string;
  street?: string;
  city?: string;
  postcode?: string;
  country?: string;
}

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  profile: CustomerProfile | null;
  loading: boolean;
  isAuthenticated: boolean;
  setProfile: (p: CustomerProfile | null) => void;
  refreshProfile: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  session: null,
  profile: null,
  loading: true,
  isAuthenticated: false,
  setProfile: () => {},
  refreshProfile: async () => {},
  logout: async () => {},
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfileState] = useState<CustomerProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const loadProfile = useCallback(async (u: User | null) => {
    if (!u) { setProfileState(null); return; }
    const phone = u.phone ? `+${u.phone.replace(/^\+/, '')}` : '';
    try {
      // Customer profile row may not exist on first login — handled by the
      // bootstrap RPC the AuthPage runs right after a successful OTP verify.
      const { data } = await supabase.rpc('current_customer_profile');
      const row: any = Array.isArray(data) ? data[0] : data;
      if (row) {
        setProfileState({
          name: row.full_name || '',
          phone: row.phone || phone,
          email: row.email || u.email || '',
          street: row.address_line || '',
          city: row.city || '',
          postcode: row.postcode || '',
          country: row.country || 'SA',
        });
      } else {
        setProfileState({
          name: '',
          phone,
          email: u.email || '',
        });
      }
    } catch (err) {
      console.error('[auth] loadProfile failed:', err);
      setProfileState({ name: '', phone, email: u.email || '' });
    }
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      const { data } = await supabase.auth.getSession();
      if (!alive) return;
      setSession(data.session);
      setUser(data.session?.user ?? null);
      await loadProfile(data.session?.user ?? null);
      setLoading(false);
    })();

    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      setUser(s?.user ?? null);
      void loadProfile(s?.user ?? null);
    });

    return () => { alive = false; sub.subscription.unsubscribe(); };
  }, [loadProfile]);

  const setProfile = useCallback((p: CustomerProfile | null) => {
    setProfileState(p);
  }, []);

  const refreshProfile = useCallback(async () => {
    await loadProfile(user);
  }, [loadProfile, user]);

  const logout = useCallback(async () => {
    await supabase.auth.signOut();
    setProfileState(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        loading,
        isAuthenticated: !!user,
        setProfile,
        refreshProfile,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth(): AuthContextValue {
  return useContext(AuthContext);
}
